import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Monitor, Image, Settings, BarChart3, Layout, ArrowRight, Megaphone, ShoppingCart } from 'lucide-react';
import { HitApi } from '../../../Api/ApiHit';
import { searchKiosk } from '../../../constant/Constant';

const KioskDashboard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [kiosk, setKiosk] = useState(null);

  useEffect(() => {
    fetchKioskDetails();
  }, [id]);

  const fetchKioskDetails = async () => {
    try {
      setLoading(true);

      const requestPayload = {
        page: 1,
        limit: 1,
        search: {
          _id: id,
          isActive: true
        }
      };

      console.log('📤 Fetching kiosk details:', requestPayload);

      const response = await HitApi(requestPayload, searchKiosk);

      console.log('📡 Kiosk details response:', response);

      if (response.success && response.data && response.data.length > 0) {
        setKiosk(response.data[0]);
        console.log('✅ Kiosk details loaded:', response.data[0]);
      }
    } catch (error) {
      console.error('❌ Error fetching kiosk details:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Monitor className="w-16 h-16 text-blue-600 animate-pulse mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!kiosk) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate('/kiosk-management')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <h3 className="text-xl font-bold text-gray-900">Kiosk Not Found</h3>
          </div>
        </div>
      </div>
    );
  }

  const managementCards = [
    {
      title: 'Banner Management',
      description: 'Manage banners and promotional images for this kiosk',
      icon: Image,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      onClick: () => navigate(`/kiosk-management/dashboard/${id}/banners`),
    },
    {
      title: 'Ads Management',
      description: 'Manage advertisements and promotional content',
      icon: Megaphone,
      iconBg: 'bg-pink-100',
      iconColor: 'text-pink-600',
      onClick: () => navigate(`/kiosk-management/dashboard/${id}/ads`),
    },
    {
      title: 'Kios Quicksale',
      description: 'Quick sale and billing for kiosk customers',
      icon: ShoppingCart,
      iconBg: 'bg-teal-100',
      iconColor: 'text-teal-600',
      onClick: () => navigate(`/kiosk-management/dashboard/${id}/quicksale`),
    },
    {
      title: 'Content Management',
      description: 'Manage home screen content and layout',
      icon: Layout,
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      onClick: () => console.log('Navigate to Content Management'),
    },
    {
      title: 'Settings',
      description: 'Configure kiosk settings and preferences',
      icon: Settings,
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      onClick: () => console.log('Navigate to Settings'),
    },
    {
      title: 'Analytics',
      description: 'View kiosk usage statistics and reports',
      icon: BarChart3,
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      onClick: () => console.log('Navigate to Analytics'),
      comingSoon: true,
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header with Kiosk Name and Status */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">{kiosk.kioskName}</h1>
            {kiosk.isOnline ? (
              <span className="bg-green-100 text-green-800 text-sm font-semibold px-3 py-1 rounded-full">
                Online
              </span>
            ) : (
              <span className="bg-gray-100 text-gray-600 text-sm font-semibold px-3 py-1 rounded-full">
                Offline
              </span>
            )}
          </div>
          <button
            onClick={() => navigate('/kiosk-management')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>

        {/* Management Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {managementCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <div
                key={index}
                onClick={() => !card.comingSoon && card.onClick()}
                className={`bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100 ${
                  !card.comingSoon ? 'cursor-pointer hover:scale-105' : 'opacity-75'
                }`}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`${card.iconBg} p-3 rounded-lg`}>
                      <Icon className={`w-6 h-6 ${card.iconColor}`} />
                    </div>
                    {card.comingSoon ? (
                      <span className="bg-yellow-100 text-yellow-800 text-xs font-semibold px-2 py-1 rounded-full">
                        Coming Soon
                      </span>
                    ) : (
                      <ArrowRight className="w-5 h-5 text-gray-400" />
                    )}
                  </div>

                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {card.title}
                  </h3>

                  <p className="text-gray-600 text-sm">
                    {card.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default KioskDashboard;
